/**
 * ScheduleCardsMobile — Card de agendamento para o layout mobile.
 *
 * Layout: coluna de data colorida (status) · área de conteúdo com médico, paciente,
 *         especialidade e tipo de atendimento (ícone + tooltip) · rodapé de ações.
 *
 * Toda a lógica de negócio (pagamento, teleconsulta, cancel dialog) está preservada.
 */
import {
  AttachMoney,
  Chat,
  Home,
  LocalHospital,
  VideoCameraFront,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { useState, useEffect } from "react";

import { ScreenDetailsScheduleMobile } from "./ScreenDetailsScheduleMobile";
import { useFormatter } from "../../utils/useFormatter";
import ScheduleCancelDialog from "./ScheduleCancelDialog";
import VideoConferencia from "../videoConferencia/VideoConferencia";
import { useSessionContext } from "../../contexts/SessionContext";
import { useFhirData } from "../../utils/useFhirData";
import { useLocationContext } from "../../contexts/LocationContext";
import { marketplaceService } from "@/services/marketplace/MarketplaceService";
import { GetPaymentNotice } from "@/services/fhir/paymentnotice/GetPaymentNotice";

interface IProps {
  data: any;
  /** selectedCardData do Schedule — array com [AppointmentResource, LocationResource, ...] */
  selected: any;
  setRefetch: any;
  onClick: () => void;
  /** Layout enxuto para PatientDash mobile */
  compact?: boolean;
  /** Card dentro do slider horizontal (largura 100%, sem margem inferior) */
  inSlider?: boolean;
}

// ─── Paleta de status ─────────────────────────────────────────────────────────

type StatusKey = "booked" | "arrived" | "pending" | "proposed" | "cancelled";

const STATUS_STYLE: Record<StatusKey, {
  accent: string;
  chipColor: "success" | "default" | "warning" | "error" | "primary";
  label: string;
}> = {
  booked:    { accent: "#007C6D", chipColor: "success", label: "Confirmado" },
  arrived:   { accent: "#007C6D", chipColor: "success", label: "Confirmado" },
  // Pendente mantém a cor warning (amber) como solicitado
  pending:   { accent: "#D97706", chipColor: "warning", label: "Pendente"   },
  proposed:  { accent: "#D97706", chipColor: "warning", label: "Proposta"   },
  cancelled: { accent: "#DC2626", chipColor: "error",   label: "Cancelado"  },
};

// ─── Ícones e labels por tipo de atendimento ──────────────────────────────────

type AttendanceChannel = "chat" | "teleatendimento" | "presencial" | "domicilio";

const ATTENDANCE_CONFIG: Record<AttendanceChannel, { Icon: React.ElementType; label: string }> = {
  chat:            { Icon: Chat,             label: "Chat"                    },
  teleatendimento: { Icon: VideoCameraFront, label: "Teleconsulta por vídeo"  },
  presencial:      { Icon: LocalHospital,    label: "Consulta presencial"     },
  domicilio:       { Icon: Home,             label: "Atendimento domiciliar"  },
};

// ─── Componente principal ─────────────────────────────────────────────────────

export function ScheduleCardsMobile({
  data,
  selected,
  setRefetch,
  onClick,
  compact = false,
  inSlider = false,
}: IProps) {
  const [openDetailsSchedule, setOpenDetailsSchedule] = useState<boolean>(false);
  const [scheduleCancelDialog, setScheduleCancelDialog] = useState<boolean>(false);
  const [hasPaymentLink, setHasPaymentLink] = useState<boolean>(false);
  const [payment, setPayment] = useState<any>([]);
  const [isPaid, setIsPaid] = useState<boolean>(false);

  const { userPatient } = useSessionContext();
  const { location } = useLocationContext();
  const theme = useTheme();

  // O card está selecionado quando o ID do appointment bate com o ID do selected[0]
  const isSelected = selected?.[0]?.id === data?.id;

  // Verifica existência de link de pagamento para consultas pendentes
  useEffect(() => {
    const checkPaymentLink = async () => {
      try {
        if (data?.slot?.split("/")?.[1]) {
          await marketplaceService.getPaymentLinkBySlotId(data?.slot?.split("/")?.[1]);
          setHasPaymentLink(true);
        }
      } catch {
        setHasPaymentLink(false);
      }
    };
    if (data?.status === "pending" && data?.slot) checkPaymentLink();
  }, [data?.slot, data?.status]);

  // Busca registro de pagamento associado ao agendamento
  useEffect(() => {
    const fetchPayment = async () => {
      if (!data?.id || !userPatient?.id) return;
      try {
        const filter = `status=active&_sort=-_lastUpdated&request=${data.slot}&response=Patient/${userPatient.id}&_count=1`;
        const response = await GetPaymentNotice({ filter });
        if (response?.data?.entry) {
          setPayment(response.data.entry);
          setIsPaid(!!response.data.entry[0]?.resource?.paymentStatus);
        } else {
          setPayment([]);
          setIsPaid(false);
        }
      } catch {
        setPayment([]);
        setIsPaid(false);
      }
    };
    fetchPayment();
  }, [data?.id, data?.slot, userPatient?.id]);

  // Localização e canal de atendimento para teleconsulta / tipo de serviço
  const mobileLocationResource = location?.find((loc: any) =>
    loc?.resource?.id.includes(data?.locationId?.id),
  )?.resource;
  const locationTeleconsulting    = useFhirData.virtualService(mobileLocationResource);
  const locationAttendanceChannel = mobileLocationResource?.extension?.find(
    (ext: any) => ext.url?.includes("attendance-channel"),
  )?.valueCode as AttendanceChannel | undefined;

  // Resolve o tipo de atendimento para exibir o ícone correto
  const attendanceKey: AttendanceChannel =
    locationAttendanceChannel ??
    (locationTeleconsulting ? "teleatendimento" : "presencial");
  const attendance = ATTENDANCE_CONFIG[attendanceKey];

  const handleOpenCancelDialog = () => setScheduleCancelDialog(true);

  // Estilo do status atual
  const status = (data?.status as StatusKey) ?? "booked";
  const style  = STATUS_STYLE[status] ?? STATUS_STYLE.booked;

  // Partes da data para a coluna lateral
  const dt        = dayjs(data?.startTime).locale("pt-br");
  const dayName   = dt.format("ddd").replace(".", "").toUpperCase();   // "TER"
  const dayNumber = dt.format("DD");                                    // "15"
  const monthAbbr = dt.format("MMM").replace(".", "").toUpperCase();   // "ABR"
  const time      = dt.format("HH:mm");                                 // "14:30"

  const showPayButton   = data?.status === "pending" && hasPaymentLink;
  const showVideoButton = locationTeleconsulting && !showPayButton;

  const dateColWidth = compact ? 48 : 52;
  const cardRadius = compact ? "10px" : "12px";

  return (
    <>
      <Box
        onClick={onClick}
        sx={{
          display: "flex",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          minHeight: compact ? 82 : 96,
          borderRadius: cardRadius,
          overflow: "hidden",
          border: "1px solid",
          borderColor: isSelected ? style.accent : "divider",
          bgcolor: "background.paper",
          cursor: "pointer",
          transition: "border-color 0.15s ease, box-shadow 0.15s ease",
          boxShadow: isSelected
            ? `0 1px 4px ${alpha(style.accent, 0.14)}`
            : "none",
          mb: inSlider ? 0 : compact ? 0.5 : 1,
          boxSizing: "border-box",
          "&:hover": {
            borderColor: alpha(style.accent, 0.45),
            boxShadow: `0 2px 8px ${alpha(style.accent, 0.1)}`,
          },
        }}
      >
        {/* ── Coluna de data — ultra compacta no dash ── */}
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            width: dateColWidth,
            flexShrink: 0,
            alignSelf: "stretch",
            bgcolor: isSelected ? style.accent : alpha(style.accent, 0.88),
            py: compact ? 0.75 : 1.1,
            gap: compact ? 0.12 : 0.15,
          }}
        >
          <Typography
            sx={{
              color: "rgba(255,255,255,0.82)",
              fontSize: compact ? "0.5rem" : "0.54rem",
              fontWeight: 700,
              letterSpacing: "0.06em",
            }}
          >
            {dayName}
          </Typography>
          <Typography
            sx={{
              color: "#fff",
              fontSize: compact ? "1.15rem" : "1.4rem",
              fontWeight: 800,
              lineHeight: 1,
            }}
          >
            {dayNumber}
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.75)",
              fontSize: compact ? "0.5rem" : "0.54rem",
              letterSpacing: "0.04em",
            }}
          >
            {monthAbbr}
          </Typography>
          <Divider sx={{ width: "50%", borderColor: "rgba(255,255,255,0.3)", my: compact ? 0.2 : 0.35 }} />
          <Typography
            sx={{
              color: "#fff",
              fontSize: compact ? "0.66rem" : "0.72rem",
              fontWeight: 700,
              lineHeight: 1.1,
            }}
          >
            {time}
          </Typography>
        </Stack>

        {/* ── Área de conteúdo ── */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            bgcolor: isSelected ? alpha(style.accent, 0.03) : "transparent",
            py: compact ? 0.75 : 1,
            px: compact ? 1 : 1.25,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              color="text.primary"
              sx={{
                lineHeight: 1.2,
                wordBreak: "break-word",
                fontSize: compact ? "0.78rem" : "0.8rem",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {data?.practitioner
                ? /^dr\(a\)\./i.test(data.practitioner.trim())
                  ? data.practitioner
                  : `Dr(a). ${data.practitioner.replace(/^dr\.?\s*/i, "").trim()}`
                : "Profissional não informado"}
            </Typography>

            {data?.patientName && (
              <Typography
                variant="body2"
                color="text.secondary"
                noWrap
                sx={{ mt: 0.15, fontWeight: 500, fontSize: compact ? "0.7rem" : "0.75rem" }}
              >
                {useFormatter.formatNameDisplay(data.patientName)}
              </Typography>
            )}

            {data?.healthcare && (
              <Typography
                variant="caption"
                color="text.disabled"
                noWrap
                display="block"
                sx={{ mt: 0.1, fontSize: compact ? "0.62rem" : "0.64rem" }}
              >
                {data.healthcare}
              </Typography>
            )}

            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.25, gap: 0.35 }}>
              <Tooltip title={attendance.label} placement="bottom-start">
                <Stack direction="row" alignItems="center" spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
                  <attendance.Icon sx={{ fontSize: compact ? 12 : 12, color: style.accent, flexShrink: 0 }} />
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{
                      color: "text.secondary",
                      fontWeight: 500,
                      fontSize: compact ? "0.58rem" : "0.6rem",
                      minWidth: 0,
                    }}
                  >
                    {compact ? attendance.label.split(" ")[0] : attendance.label}
                  </Typography>
                </Stack>
              </Tooltip>
              <Chip
                label={style.label}
                size="small"
                color={style.chipColor}
                sx={{
                  height: compact ? 18 : 18,
                  fontSize: compact ? "0.56rem" : "0.58rem",
                  fontWeight: 700,
                  flexShrink: 0,
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
            </Stack>
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="nowrap"
            gap={0.35}
            sx={{ mt: compact ? 0.5 : 0.65, pt: 0.25 }}
          >
            <Button
              size="small"
              variant="text"
              onClick={(e) => { e.stopPropagation(); setOpenDetailsSchedule(true); onClick(); }}
              sx={{
                px: 0,
                minWidth: 0,
                fontWeight: 600,
                fontSize: compact ? "0.58rem" : "0.58rem",
                color: style.accent,
                flexShrink: 0,
              }}
            >
              Detalhes →
            </Button>

            <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0, ml: "auto" }}>
              {showPayButton && (
                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  startIcon={
                    <AttachMoney sx={{ fontSize: compact ? 14 : 15, color: "#fff !important" }} />
                  }
                  onClick={async (e) => {
                    e.stopPropagation();
                    const link = await marketplaceService.getPaymentLinkBySlotId(data?.slot.split("/")?.[1]);
                    window.open(link, "_blank");
                  }}
                  sx={{
                    bgcolor: "warning.main",
                    color: "#fff",
                    fontSize: compact ? "0.66rem" : "0.68rem",
                    fontWeight: 700,
                    borderRadius: "8px",
                    py: compact ? 0.25 : 0.35,
                    px: compact ? 0.75 : 0.85,
                    minWidth: 0,
                    boxShadow: "none",
                    transition: "transform 0.12s ease, box-shadow 0.12s ease",
                    "&:hover": {
                      bgcolor: "warning700.main",
                      color: "#fff",
                      boxShadow: "none",
                    },
                    "&:active": { transform: "scale(0.96)" },
                    "& .MuiButton-startIcon": { mr: 0.25 },
                  }}
                >
                  Pagar
                </Button>
              )}

              {showVideoButton && (
                <Box
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    "& .MuiButton-root": {
                      width: "auto !important",
                      minWidth: compact ? "34px !important" : "36px !important",
                      minHeight: compact ? "34px !important" : "36px !important",
                      px: compact ? "6px !important" : "7px !important",
                      py: compact ? "6px !important" : "7px !important",
                      borderRadius: "8px !important",
                    },
                    "& .MuiTypography-root": { display: "none !important" },
                    "& .MuiButton-startIcon": { margin: "0 !important" },
                  }}
                >
                  <VideoConferencia
                    roomUrl={data?.virtualService}
                    cardSchedule
                    isPending={data?.status === "pending"}
                    attendanceType={locationAttendanceChannel}
                    appointmentData={{ id: data.id, patientName: data.patientName, practitionerName: data.practitioner, startTime: data.startTime }}
                  />
                </Box>
              )}
            </Stack>
          </Stack>
        </Box>
      </Box>

      {/* ── Modais de detalhe e cancelamento ── */}
      <ScreenDetailsScheduleMobile
        open={openDetailsSchedule}
        isVirtual={locationTeleconsulting}
        onClose={() => setOpenDetailsSchedule(false)}
        setScheduleCancelDialog={handleOpenCancelDialog}
        data={data}
        payment={payment}
        isPaid={isPaid}
      />
      <ScheduleCancelDialog
        appointment={data}
        isOpen={scheduleCancelDialog}
        setIsOpen={setScheduleCancelDialog}
        setRefetch={setRefetch}
        payment={payment}
        isPaid={isPaid}
      />
    </>
  );
}
